// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Combat Configuration
 * 
 * Contains all combat system settings, damage calculations, and enemy mechanics.
 */

export const combatConfig = {
  // Fire mode damage multipliers
  fireModes: {
    single: 1.0,    // Single shot
    burst: 1.5,     // Burst fire (3 shots)
    salvo: 2.0      // Full salvo (all weapons)
  },
  
  // Fire mode costs (weapon energy)
  fireModeCosts: {
    single: 25,
    burst: 35,
    salvo: 50
  },
  
  // Critical hit settings
  criticalHit: {
    chance: 0.15,         // 15% chance
    damageMultiplier: 1.5 // 1.5x damage
  },
  
  // Combo system
  combo: {
    damageBonus: 0.15,    // 15% bonus per consecutive hit
    windowSeconds: 3,     // 3-second window for combos
    timeoutMs: 3000       // 3000ms timeout
  },
  
  // ACE status (unlocked after kills)
  ace: {
    killsRequired: 3,     // Kills needed to unlock
    damageBonus: 0.2,     // 20% damage bonus
    bonusMultiplier: 1.2  // 1.2x multiplier
  },
  
  // Environmental effects
  nebula: {
    missChance: 0.3       // 30% miss chance in nebula
  },
  
  // Enemy spawn settings
  enemySpawn: {
    minEnemies: 1,
    maxEnemies: 4,
    spawnDistance: 150    // Distance from player
  },
  
  // Enemy scaling based on player weapon count
  // Reduces enemy difficulty when player has fewer weapons
  enemyScaling: {
    enabled: true,
    baseWeaponCount: 4,           // Full difficulty at 4 weapons
    healthScaling: {
      1: 0.5,   // 1 weapon: 50% enemy health
      2: 0.7,   // 2 weapons: 70% enemy health
      3: 0.85,  // 3 weapons: 85% enemy health
      4: 1.0    // 4 weapons: 100% enemy health (full difficulty)
    },
    countScaling: {
      1: { min: 1, max: 1 },  // 1 weapon: always 1 enemy
      2: { min: 1, max: 2 },  // 2 weapons: 1-2 enemies
      3: { min: 1, max: 3 },  // 3 weapons: 1-3 enemies
      4: { min: 1, max: 4 }   // 4 weapons: 1-4 enemies (full)
    }
  },
  
  // Enemy stats by threat level
  enemyStats: {
    CRITICAL: { shields: 250, hull: 250 },
    HIGH: { shields: 200, hull: 200 },
    MEDIUM: { shields: 150, hull: 150 },
    LOW: { shields: 100, hull: 100 }
  },
  
  // Enemy return fire
  returnFire: {
    chance: 0.4,          // 40% chance to return fire
    delayMs: 800,         // Delay before return fire
    damage: {
      CRITICAL: 15,
      HIGH: 10,
      MEDIUM: 7,
      LOW: 5
    }
  },
  
  // Combat rewards
  rewards: {
    creditsMin: 50,       // Minimum credits per kill
    creditsMax: 150       // Maximum credits per kill
  },
  
  // Combat escape
  escape: {
    fuelCost: 15,         // Fuel cost to escape (percentage)
    delayMs: 2000         // Delay before escape completes
  },
  
  // Weapon charging animation
  weaponCharge: {
    intervalMs: 100,      // Charge animation interval
    incrementPerTick: 5,  // Charge increase per tick
    basePower: 50         // Base power for damage calculations
  },
  
  // Visual effects
  visuals: {
    weaponSpacing: 15,        // Horizontal spacing between weapons
    weaponSpread: 10,         // Vertical spread for weapon fire
    shotStagger: 100,         // Delay between shots in ms
    enemySpacing: 8,          // Spacing for enemy positioning
    powerBonus: 100           // Divisor for weapon power bonus (weaponPower / 100)
  }
};
